import { Box, Typography } from '@mui/material';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { StandardizedVoyagesDataGrid } from '../../components/DataGrids/StandardizedVoyagesDataGrid';
import { StandardizedVoyageEditDialog } from '../../components/Dialogs/StandardizedVoyageEditDialog';
import { VoyageService } from '../../services/voyageService';
import { useUserScope } from '../../hooks/useUserScope';
import ConfirmationPopover from '../../components/ConfirmationPopover/ConfirmationPopover';
import { useConfirmationPopover } from '../../hooks/useConfirmationPopover';
import { ToastService } from '../../services/toastService';

import type { StandardizedVoyageWithVessel } from '@/types';

export const VoyagesPage = () => {
  const [editingVoyage, setEditingVoyage] = useState<StandardizedVoyageWithVessel | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [voyageToDelete, setVoyageToDelete] = useState<StandardizedVoyageWithVessel | null>(null);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'atd', sort: 'desc' }]);
  const [pageTokens, setPageTokens] = useState<Record<number, string>>({});

  const queryClient = useQueryClient();

  // Confirmation popover for delete action
  const { anchorEl, isOpen, openConfirmation, closeConfirmation } = useConfirmationPopover();

  const { effectiveUserId } = useUserScope();
  // Admin-only app: effectiveUserId null => all voyages
  const scopeKey = effectiveUserId ?? 'ALL';

  // Get current sort parameters
  const sortBy = sortModel.length > 0 ? sortModel[0].field : 'atd';
  const sortDirection = sortModel.length > 0 ? sortModel[0].sort || 'desc' : 'desc';

  const currentPageToken = pageTokens[paginationModel.page] || undefined;

  // Fetch voyages with pagination
  const {
    data: voyageResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'voyages',
      {
        userId: scopeKey,
        pageSize: paginationModel.pageSize,
        pageToken: currentPageToken,
        sortBy,
        sortDirection,
      },
    ],
    queryFn: () =>
      VoyageService.getAllStandardizedVoyagesPaginated(
        effectiveUserId,
        paginationModel.pageSize,
        currentPageToken,
        sortBy,
        sortDirection
      ),
    enabled: true,
    placeholderData: (previousData) => previousData, // Keep previous data while loading new page
  });

  // Update page tokens when we get a response
  useEffect(() => {
    if (voyageResponse?.next_page_token && paginationModel.page >= 0) {
      const nextPage = paginationModel.page + 1;
      setPageTokens((prev) => ({
        ...prev,
        [nextPage]: voyageResponse.next_page_token!,
      }));
    }
  }, [voyageResponse?.next_page_token, paginationModel.page]);

  // Handle query errors with toast notifications
  useEffect(() => {
    if (isError && error) {
      console.error('Failed to load voyages:', error);
      ToastService.handleApiError(error, 'Loading Voyages');
    }
  }, [isError, error]);

  const voyages = voyageResponse?.voyages || [];
  const totalCount = voyageResponse?.total_count || 0;

  // Pagination handlers
  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      // Prevent unnecessary updates if page/pageSize hasn't actually changed
      if (
        newModel.page === paginationModel.page &&
        newModel.pageSize === paginationModel.pageSize
      ) {
        return;
      }

      // If page size changed, reset to first page and clear tokens
      if (newModel.pageSize !== paginationModel.pageSize) {
        setPaginationModel({ page: 0, pageSize: newModel.pageSize });
        setPageTokens({});
      } else {
        setPaginationModel(newModel);
      }
    },
    [paginationModel.page, paginationModel.pageSize]
  );

  const handleSortModelChange = useCallback(
    (newModel: GridSortModel) => {
      // Reset to first page when sorting changes
      if (
        newModel.length > 0 &&
        (newModel[0].field !== sortBy || newModel[0].sort !== sortDirection)
      ) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        // Clear page tokens since we're starting fresh with new sort
        setPageTokens({});
      }
      setSortModel(newModel);
    },
    [sortBy, sortDirection]
  );

  // Delete voyage mutation
  const deleteVoyageMutation = useMutation({
    mutationFn: (data: { voyageId: string; vesselId: string }) =>
      VoyageService.deleteStandardizedVoyage(data.voyageId, data.vesselId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['voyages', { userId: scopeKey }],
      });
      // Invalidate dashboard stats since it shows voyage counts
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      ToastService.success('Voyage deleted successfully', 'Voyage Deleted');
    },
    onError: (error: unknown) => {
      console.error('Failed to delete voyage:', error);
      ToastService.handleApiError(error, 'Voyage Deletion');
    },
  });

  const handleEdit = (voyage: StandardizedVoyageWithVessel) => {
    setEditingVoyage(voyage);
    setIsEditDialogOpen(true);
  };

  const handleDelete = (
    voyage: StandardizedVoyageWithVessel,
    event?: React.MouseEvent<HTMLElement>
  ) => {
    if (event && event.currentTarget) {
      setVoyageToDelete(voyage);
      openConfirmation(event.currentTarget);
    }
  };

  const confirmDelete = () => {
    if (voyageToDelete) {
      deleteVoyageMutation.mutate({
        voyageId: voyageToDelete.voyageID,
        vesselId: voyageToDelete.vesselID,
      });
      setVoyageToDelete(null);
    }
    closeConfirmation();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Voyages Management</Typography>
      </Box>

      <StandardizedVoyagesDataGrid
        voyages={voyages}
        loading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        totalCount={totalCount}
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        onPaginationModelChange={handlePaginationModelChange}
        onSortModelChange={handleSortModelChange}
      />

      {/* Edit Voyage Dialog */}
      <StandardizedVoyageEditDialog
        open={isEditDialogOpen}
        voyage={editingVoyage}
        onClose={() => {
          setIsEditDialogOpen(false);
          setEditingVoyage(null);
        }}
      />

      {/* Delete Confirmation Popover */}
      <ConfirmationPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeConfirmation}
        onConfirm={confirmDelete}
        title="Delete Standardized Voyage"
        message={`Are you sure you want to delete voyage "${voyageToDelete?.voyageID}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="warning"
        isLoading={deleteVoyageMutation.isPending}
      />
    </Box>
  );
};
